/* eslint-disable camelcase */
import { handle } from 'redux-pack';
import {
	GET_MEDIA,
	CREATE_MEDIA,
	SAVE_MEDIA_ITEM,
	SET_SELECTED_MEDIA,
	SET_MEDIA_MODAL_OPEN,
	SET_MEDIA_SLIDE_ANIMATIONS,
	SET_MEDIA_SLIDE_DIRECTION,
	SET_MEDIA_SELECTED_PAGE,
	SET_SHARED_WITH,
	SET_SHARED_MEDIA_PAGES_VIEW_ON,
	SET_CURRENTLY_UPLOADING_MEDIA,
	UPDATE_MEDIA,
	SET_ID_OF_CURRENTLY_UPLOADING_MEDIA,
	DELETE_MEDIA_ITEM,
	SET_PRESENTED_MEDIA,
	SET_CURRENTLY_UPLOADING_IN_MODAL,
	SYNC_MEDIA_REDUCER_STATE,
	SET_PRESENTED_MEDIA_VIDEOSTATE,
	SET_PRESENTED_MEDIA_PAGE,
	ADD_PRESENTED_MEDIA,
	REMOVE_PRESENTED_MEDIA,
	SET_NOTIFY_ON_UPDATE,
	SET_MEDIA_UPDATE_EVENT_INFO,
	UPDATE_SINGLE_MEDIA,
	GET_MEDIA_PAGES_FOR_MEETING,
	SAVE_MEDIA_PAGE,
	UPDATE_MEDIA_PAGE,
	SET_SHOWBUILDER_MEDIA,
	DELETE_MEDIA_PAGES,
	SET_SLIDE_VIEW_MEDIA,
	SET_SLIDE_VIEW_MEDIA_INDEX,
	SET_SELECTED_AUDIO_MEDIA,
	REMOVE_SHOT_MODAL_MEDIA,
	CLEAR_DRAFT_MEDIA,
	MEDIA_ENTITY_UPDATED_DRAFT,
	SET_DRAFT_MEDIA,
} from '../actions/media';
import { arrayUpsert, decodeHtmlEntities, uniqueArray } from '../../utils/utils';
import { GraphicInterface } from './graphics';
import { CLOUD_CONVERT_STATUS } from '../../types/constants';
import { Entity } from '../../types/interfaces';
import { EntityType } from '../../types/enums';

interface Action {
	type: string;
	payload: any;
}

export interface ImagePickerMedia {
	id: number
	, url: string
}

export interface UserMedia {
	id: number
	, gr_user: number
	, type_table_id: number
	, url: string
}

export interface SharedMediaItemData {
	media_id: number;
	shared_by: number;
	current_page: number;
}

export interface MediaData extends MediaAudioData {
	image_array: string[]; // array of image urls
	thumbnail_map: { [key: string]: string }; // map of thumbnail urls
	duration_map?: number[]; // array of video durations in seconds, corresponding to image_array indices
	notes_map?: { [key: string]: number }; // map of thumbnail urls
	fill?: boolean; // media should fill container
	background_color?: string; // media background color
	description?: string;
}

export interface MediaAudioData {
	loop?: boolean; // video loops
    fadeIn?: number;
    fadeOut?: number;
    offsetStartSecond?: number;
    offsetEndSecond?: number;
}

export interface MediaDownloadLink {
	url: string;
	name: string;
	type: string;
	label?: string;
	order?: number;
}

export interface SlideNote {
	key: number;
	notes: string;
}

export interface MediaItem {
	id: number;
	type: string;
	title: string;
	created_date: number;
	edited_date?: number;

	edited_by?: number;
	meeting: number;
	shared_with: string | null;
	cloud_convert_id?: string | null;
	shared_by?: number | null; // the user currently sharing
	convert_status?: string;

	// file
	document: number;
	original_name?: string; // original filename
	display_name?: string;
	custom_name?: string; // set in the media modal
	location: string;
	thumbnail: string | null;
	thumbnail_status: number;
	thumbnail_url: string;
	status: number;
	filesize: number;

	// content
	num_pages: number; // total pages in media item
	endpoint: string;
	images: string; // comma deliniated string of urls
	created_by: number;

	media_data: MediaData
	recording_download_link?: string;

	// denotes the parent this media item was trimmed off of
	original_id?: number | null;
	iso_url?: string[] | null;
	number_of_isos?: number | null;
	download_name?: string;
	download_links?: MediaDownloadLink[];
	notes?: SlideNote[];

	// generated content
	template_type?: string |null;
	provider?: string | null;
}

/*
	We have:
		- `PagedMediaItemNew` (sans ID)
		- `PagedMediaItemExisting` (with ID)
	to avoid having an optional ID.
*/
export interface PagedMediaItemNew {
	mediaId: number;
	meeting: number;
	url: string;
	volume: number;
	muted: boolean;
	duration?: number | null;
}

export interface PagedMediaItemExisting extends PagedMediaItemNew {
	id: number;
}

export interface VideoState {
	timeStarted?: Date | null;
	paused: boolean;
	timeElapsed?: number | null;
	volume: number | string;
	scrubbing?: Scrubbing;
	updatedBy?: string;
	muted?: boolean;
	duration?: number | string
	mediaPage?: PagedMediaItemExisting;
}
export enum ScrubHistory {
	// eslint-disable-next-line no-unused-vars
	Playing,
	// eslint-disable-next-line no-unused-vars
	Paused
}
export interface Scrubbing {
	isScrubbing: boolean,
	history: ScrubHistory
}

export interface PresentedMediaItem {
	mediaId: number;
	currentPage: number;
	videoState?: VideoState;
	updatedBy?: number; // gr_user
	pages?: string[]; 			// image url array
	background_color?: string;	// color hex code
	fill?: boolean;				// should fill bg color
	name?: string;				// media item name
}

export interface PageDeleted {
	location: string;
}

export interface MediaUpdateEventInfo {
	mediaId: number;
	type: string;
}

export interface MediaState {
	media: MediaItem[];
	shotModalMedia: MediaItem[];
	loadingMedia: boolean;
	mediaError: string;
	savingMedia: boolean;
	savingMediaError: string;
	deletingMedia: boolean;
	deletingMediaError: string;
	deletedMedia: MediaItem[];
	selectedMedia: MediaItem | undefined;
	mediaModalOpen: boolean;
	selectedPage: number; // specifically for media modal
	slideAnimations: string;
	slideDirection: string;
	sharedMediaPagesViewOn: boolean;
	currentlyUploadingMedia: MediaItem | undefined;
	idOfCurrentlyUploadingMedia: string;
	// array of media objects currently being presented in video feeds
	presentedMedia: PresentedMediaItem[];
	currentlyUploadingInModal: number[];
	mediaLoaded: boolean;
	mediaRefreshed: boolean;
	notifyOnUpdate: boolean;
	mediaPages: PagedMediaItemExisting[];
	mediaPagesError: string;
	loadingMediaPages: boolean;
	mediaPagesRefreshed: boolean;
	savingMediaPages: boolean;
	savingMediaPagesError: string;
	mediaPagesLoaded: boolean;
	showbuilderMedia: PresentedMediaItem[];
	updateEventInfo?: MediaUpdateEventInfo;
	slideViewMedia: number | null;
	slideViewMediaIndex: number;
	selectedAudioMedia: MediaItem | undefined;
	recentSlideDeckEntity: Entity | null;
}

export interface MediaSearchResults {
	files: MediaItem[];
	recordings: MediaItem[];
	graphics: GraphicInterface[];
}

export const DIRECTION = {
	LEFT: 'left',
	RIGHT: 'right',
	UP: 'up',
	DOWN: 'down',
};

export const initialMediaState: MediaState = {
	media: [],
	shotModalMedia: [],
	loadingMedia: false,
	mediaError: '',
	savingMedia: false,
	savingMediaError: '',
	deletingMedia: false,
	deletingMediaError: '',
	deletedMedia: [],
	selectedMedia: undefined,
	mediaModalOpen: false,
	selectedPage: 1,
	slideAnimations: 'dissolve',
	slideDirection: 'right',
	sharedMediaPagesViewOn: false,
	currentlyUploadingMedia: undefined,
	idOfCurrentlyUploadingMedia: '',
	presentedMedia: [],
	currentlyUploadingInModal: [],
	mediaLoaded: false,
	mediaRefreshed: false,
	notifyOnUpdate: false,
	mediaPages: [],
	mediaPagesError: '',
	loadingMediaPages: false,
	mediaPagesRefreshed: false,
	savingMediaPages: false,
	savingMediaPagesError: '',
	mediaPagesLoaded: true,
	showbuilderMedia: [],
	slideViewMedia: null,
	slideViewMediaIndex: 0,
	selectedAudioMedia: undefined,
	recentSlideDeckEntity: null,
};

const actions: Map<any, Function> = new Map([
	[GET_MEDIA, (state: MediaState, action: Action) => handle(state, action, {
		start: (pState) => ({ ...pState, loadingMedia: true, mediaRefreshed: false }),
		finish: (pState) => ({ ...pState, loadingMedia: false }),
		failure: (pState) => ({ ...pState, mediaError: 'Error loading media. Please try again later.' }),
		success: (pState) => {
			const sanitizedPayload = decodeHtmlEntities(JSON.stringify(action.payload));

			return {
				...pState,
				mediaError: '',
				media: uniqueArray('id', JSON.parse(sanitizedPayload)),
				mediaLoaded: true,
				mediaRefreshed: true,
				shotModalMedia: state.shotModalMedia.map((media: MediaItem) => (
					media.id === action.payload.id ? action.payload : media
				)),
			};
		},
	})],
	[CREATE_MEDIA, (state: MediaState, action: Action) => handle(state, action, {
		start: (pState) => ({ ...pState, savingMedia: true }),
		finish: (pState) => ({ ...pState, savingMedia: false }),
		failure: (pState) => ({ ...pState, savingMediaError: 'Error saving media. Please try again.' }),
		success: (pState) => {
			// we ran into a problem where this action would fire, then we would load media,
			// then the action success would fire, adding the new media item to the re-loaded media,
			// which would result in a duplicate of the new media item
			const ids = new Set(pState.media.map((m) => m.id));
			const mergedMedia = [...action.payload.filter((m: any) => !ids.has(m.id)), ...state.media];
			return {
				...pState,
				currentlyUploadingMedia: undefined,
				media: mergedMedia,
				selectedMedia: state.selectedMedia?.id === 1 ? mergedMedia[0] : state.selectedMedia,
			};
		},
	})],
	[UPDATE_MEDIA, (state: MediaState, action: Action) => ({
		...state,
		media: action.payload,
		shotModalMedia: state.shotModalMedia.map((media: MediaItem) => (
			media.id === action.payload.id ? action.payload : media
		)),
	})],
	[SET_CURRENTLY_UPLOADING_MEDIA, (state: MediaState, action: Action) => (
		{ ...state, currentlyUploadingMedia: action.payload }
	)],
	[UPDATE_SINGLE_MEDIA, (state: MediaState, action: Action) => {
		const newState = { ...state };
		const sanitizedPayload = decodeHtmlEntities(JSON.stringify(action.payload));
		const parsedPayload = JSON.parse(sanitizedPayload);
		// Update saved media item inside media state
		let didExist = false;
		const media = newState.media.map((nMedia: MediaItem) => {
			if (parsedPayload.id === nMedia.id) {
				didExist = true;
				return parsedPayload;
			}
			return nMedia;
		});

		if (!didExist) {
			media.unshift(parsedPayload);
		}
		newState.media = media;

		// Update selectedMedia state if the selectedMedia is the media item that was saved
		if (newState.selectedMedia && newState.selectedMedia.id === parsedPayload.id) {
			newState.selectedMedia = parsedPayload;
		}

		if (
			parsedPayload.id === state.currentlyUploadingMedia?.id
			&& parsedPayload?.convert_status === CLOUD_CONVERT_STATUS.COMPLETE
		) {
			newState.idOfCurrentlyUploadingMedia = '';
			delete newState.currentlyUploadingMedia;
		}

		return newState;
	}],
	[SAVE_MEDIA_ITEM, (state: MediaState, action: Action) => handle(state, action, {
		start: (pState) => ({ ...pState, savingMedia: true }),
		finish: (pState) => ({ ...pState, savingMedia: false }),
		failure: (pState) => ({ ...pState, savingMediaError: 'Error saving media. Please try again.' }),
		success: (pState) => {
			const newState = { ...pState };

			// Update saved media item inside media state
			const media = newState.media.map((nMedia: MediaItem) => {
				if (action.payload.id === nMedia.id) {
					return action.payload;
				}
				return nMedia;
			});
			newState.media = media;

			// Update selectedMedia state if the selectedMedia is the media item that was saved
			if (newState.selectedMedia && newState.selectedMedia.id === action.payload.id) {
				newState.selectedMedia = action.payload;
			}

			return newState;
		},
	})],
	[SET_SELECTED_MEDIA, (state: MediaState, action: Action) => {
		const [mediaItem] = action.payload;
		return {
			...state,
			selectedMedia: mediaItem,
		};
	}],
	[SET_MEDIA_MODAL_OPEN, (state: MediaState, action: Action) => {
		const [open, mediaItem] = action.payload;
		return {
			...state,
			mediaModalOpen: open,
			selectedMedia: mediaItem,
		};
	}],
	[SET_MEDIA_SELECTED_PAGE, (state: MediaState, action: Action) => (
		{ ...state, selectedPage: action.payload }
	)],
	[SET_MEDIA_SLIDE_ANIMATIONS, (state: MediaState, action: Action) => (
		{ ...state, slideAnimations: action.payload }
	)],
	[SET_MEDIA_SLIDE_DIRECTION, (state: MediaState, action: Action) => (
		{ ...state, slideDirection: action.payload }
	)],
	[SET_SHARED_WITH, (state: MediaState, action: Action) => (
		{ ...state, selectedMedia: { ...state.selectedMedia, shared_with: action.payload } }
	)],
	[SET_SHARED_MEDIA_PAGES_VIEW_ON, (state: MediaState, action: Action) => (
		{ ...state, sharedMediaPagesViewOn: action.payload }
	)],
	[SET_ID_OF_CURRENTLY_UPLOADING_MEDIA, (state: MediaState, action: Action) => (
		{ ...state, idOfCurrentlyUploadingMedia: action.payload }
	)],
	[SET_PRESENTED_MEDIA, (state: MediaState, action: Action) => (
		{ ...state, presentedMedia: [...action.payload] }
	)],
	[DELETE_MEDIA_ITEM, (state: MediaState, action: Action) => {
		const newState = { ...state };
		newState.media = newState.media.filter((mediaItem) => mediaItem.id !== action.payload);
		return newState;
	}],
	[SET_CURRENTLY_UPLOADING_IN_MODAL, (state: MediaState, action: Action) => (
		{ ...state, currentlyUploadingInModal: action.payload }
	)],
	[SYNC_MEDIA_REDUCER_STATE, (state: MediaState, action: Action) => (
		{ ...state, ...action.payload }
	)],
	[SET_PRESENTED_MEDIA_VIDEOSTATE, (state: MediaState, action: Action) => {
		const mediaIndex = state.presentedMedia.findIndex(
			(spm) => spm.mediaId === action.payload.mediaId,
		);
		const newPresMedia = state.presentedMedia;
		if (mediaIndex !== -1) {
			const oldVideoState = newPresMedia[mediaIndex].videoState;
			const newVideoState = action.payload.videoState;
			Object.keys(newVideoState).forEach((key: string) => (
				newVideoState[key] === undefined && delete newVideoState[key]
			));
			newPresMedia[mediaIndex].videoState = { ...oldVideoState, ...newVideoState };
		}
		// spread operator required on newPresMedia to 'kick' hook's dependencies
		return { ...state, presentedMedia: [...newPresMedia] };
	}],
	[SET_PRESENTED_MEDIA_PAGE, (state: MediaState, action: Action) => {
		const newPresentedMedia = state.presentedMedia.map((m: PresentedMediaItem) => {
			const newM = m;
			if (newM.mediaId === action.payload.mediaId) {
				newM.currentPage = action.payload.currentPage;
				newM.videoState = { ...newM.videoState, ...action.payload.videoState };
			}
			return newM;
		});
		return { ...state, presentedMedia: [...newPresentedMedia] };
	}],
	[ADD_PRESENTED_MEDIA, (state: MediaState, action: Action) => {
		const newState = { ...state };
		newState.presentedMedia = [...newState.presentedMedia, action.payload];
		return newState;
	}],
	[REMOVE_PRESENTED_MEDIA, (state: MediaState, action: Action) => {
		const newState = { ...state };
		newState.presentedMedia = newState.presentedMedia.filter((pm) => pm.mediaId !== action.payload);
		return newState;
	}],
	[SET_NOTIFY_ON_UPDATE, (state: MediaState, action: Action) => (
		{ ...state, notifyOnUpdate: action.payload }
	)],
	[SET_MEDIA_UPDATE_EVENT_INFO, (state: MediaState, action: Action) => (
		{ ...state, updateEventInfo: action.payload }
	)],
	[GET_MEDIA_PAGES_FOR_MEETING, (state: MediaState, action: Action) => handle(state, action, {
		start: (mediaState) => ({
			...mediaState,
			loadingMediaPages: true,
			mediaPagesRefreshed: false,
		}),
		finish: (mediaState) => ({
			...mediaState,
			loadingMediaPages: false,
		}),
		failure: (mediaState) => ({
			...mediaState,
			mediaPagesError: 'Error loading media pages.',
			mediaPagesLoaded: false,
		}),
		success: (mediaState) => ({
			...mediaState,
			mediaPages: action.payload as PagedMediaItemExisting[],
			mediaPagesError: '',
			mediaPagesRefreshed: true,
			mediaPagesLoaded: true,
		}),
	})],
	[SAVE_MEDIA_PAGE, (state: MediaState, action: Action) => handle(state, action, {
		start: (mediaState) => ({
			...mediaState,
			savingMediaPages: true,
		}),
		finish: (mediaState) => ({
			...mediaState,
			savingMediaPages: false,
		}),
		failure: (mediaState) => ({
			...mediaState,
			savingMediaPagesError: 'Error saving media pages.',

		}),
		success: (mediaState) => {
			const mediaPagesCopy = [
				...mediaState.mediaPages,
			];
			const updatedMediaState = {
				...mediaState,
				mediaPages: arrayUpsert<PagedMediaItemExisting>(
					mediaPagesCopy,
					action.payload as PagedMediaItemExisting,
					'id',
				),
			};

			return updatedMediaState;
		},
	})],
	[UPDATE_MEDIA_PAGE, (state: MediaState, action: Action) => {
		const mediaPagesCopy = [
			...state.mediaPages,
		];
		const updatedMediaPage: PagedMediaItemExisting = action.payload;
		const currentIndex = mediaPagesCopy.findIndex(
			({ id }) => id === updatedMediaPage.id,
		);

		// If not found
		if (currentIndex === -1) {
			return {
				...state,
				mediaPages: [...mediaPagesCopy, updatedMediaPage],
			};
		}

		mediaPagesCopy[currentIndex] = updatedMediaPage;

		return {
			...state,
			mediaPages: mediaPagesCopy,
		};
	}],
	[DELETE_MEDIA_PAGES, (state: MediaState, action: Action): MediaState => {
		const idsToDelete: number[] = action.payload;

		if (idsToDelete.length) {
			return {
				...state,
				mediaPages: state.mediaPages.filter(
					({ id }) => !idsToDelete.includes(id),
				),
			};
		}

		return state;
	}],
	[SET_SHOWBUILDER_MEDIA, (state: MediaState, action: Action): MediaState => (
		{
			...state,
			showbuilderMedia: action.payload,
		}
	)],
	[SET_SLIDE_VIEW_MEDIA, (state: MediaState, action: Action): MediaState => (
		{
			...state,
			slideViewMedia: action.payload,
			slideViewMediaIndex: action.payload ? state.slideViewMediaIndex : 0,
		}
	)],
	[SET_SLIDE_VIEW_MEDIA_INDEX, (state: MediaState, action: Action): MediaState => (
		{
			...state,
			slideViewMediaIndex: action.payload,
		}
	)],
	[SET_SELECTED_AUDIO_MEDIA, (state: MediaState, action: Action) => ({
		...state,
		selectedAudioMedia: action.payload,
	})],
	[EntityType.recentSlideDeck, (state: MediaState, action: Action) => ({
		...state,
		recentSlideDeckEntity: action.payload,
	})],
	[REMOVE_SHOT_MODAL_MEDIA, (state: MediaState, action: Action) => ({
		...state,
		shotModalMedia: state.shotModalMedia.filter(
			(media: MediaItem) => media.id !== action.payload
		),
	})],
	[MEDIA_ENTITY_UPDATED_DRAFT, (state: MediaState, action: Action) => ({
		...state,
		shotModalMedia: state.shotModalMedia.reduce(
			(newArray: MediaItem[], existingMedia: MediaItem) => {
				if (newArray[0].id === existingMedia.id) {
					const newMedia = newArray.shift();
					if (newMedia) newArray.push(newMedia);
				} else {
					newArray.push(existingMedia);
				}
				return newArray;
			}, [decodeHtmlEntities(action.payload.entityValue)],
		),
	})],
	[SET_DRAFT_MEDIA, (state: MediaState, action: Action) => ({
		...state,
		shotModalMedia: action.payload,
	})],
	[CLEAR_DRAFT_MEDIA, (state: MediaState) => ({
		...state,
		shotModalMedia: [],
	})],
]);

// eslint-disable-next-line default-param-last
export function MediaReducer(state: any = initialMediaState, action: Action) {
	return actions.get(action.type)?.(state, action) || state;
}
